/** Concierge widget for the Services home.
 *  Natural language → LLM-parsed service suggestion with confirm-before-navigate.
 *  Uses /api/services/concierge/parse (Claude via Emergent LLM Key). */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, ArrowRight, Loader2, MessageSquare } from "lucide-react";

const EXAMPLES = [
  "Get a laptop for a new joiner in Bangalore next Monday",
  "Recover MacBook from a departing employee",
  "Refresh 50 devices at Chennai office",
  "Dispose of 100 old laptops securely",
];

export default function ConciergeWidget() {
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const ask = async () => {
    if (!text.trim() || busy) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const { data } = await api.post("/services/concierge/parse", { text: text.trim() });
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Concierge is unavailable right now.");
    } finally { setBusy(false); }
  };

  const goToSuggestion = () => {
    if (!result?.code) return;
    if (result.suggestion_type === "bundle") nav(`/services/bundle/${result.code}`);
    else if (result.suggestion_type === "service") nav(`/services/detail/${result.code}`);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="concierge-widget">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-3.5 w-3.5 text-[color:var(--st-accent)]" strokeWidth={1.6} />
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Ask ONE Concierge</div>
      </div>
      <div className="flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="Describe what you need — e.g. 'onboard 12 developers in Chennai'"
          className="flex-1 h-11 px-3 rounded-md border border-slate-200 bg-white text-sm placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/5"
          data-testid="concierge-input"
        />
        <button
          onClick={ask}
          disabled={busy || !text.trim()}
          className="h-11 px-4 rounded-md bg-slate-900 text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-slate-800 disabled:opacity-50"
          data-testid="concierge-ask"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><MessageSquare className="h-4 w-4" /> Ask</>}
        </button>
      </div>

      {!result && !error && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              onClick={() => { setText(ex); }}
              className="text-[11px] text-slate-500 hover:text-slate-900 bg-slate-50 border border-slate-100 rounded-full px-2.5 py-1"
              data-testid="concierge-example"
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded-md px-3 py-2" data-testid="concierge-error">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-3 border border-slate-200 rounded-md p-3 bg-slate-50" data-testid="concierge-result">
          {result.suggestion_type === "none" || !result.code ? (
            <div className="text-sm text-slate-700">
              I couldn't find a strong match. Try mentioning what you want to do
              — e.g. "recover", "deploy", "refresh" — and how many.
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
                    Suggested {result.suggestion_type}
                    {typeof result.confidence === "number" && (
                      <span className="ml-2 text-slate-400">{Math.round(result.confidence * 100)}% match</span>
                    )}
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mt-0.5">
                    {result.resolved?.customer_facing_name || result.resolved?.name || result.code}
                  </div>
                  {result.rationale && <div className="text-xs text-slate-500 mt-1">{result.rationale}</div>}
                </div>
                <button
                  onClick={goToSuggestion}
                  className="h-9 px-3 rounded-md bg-slate-900 text-white text-xs font-medium inline-flex items-center gap-1.5 hover:bg-slate-800 shrink-0"
                  data-testid="concierge-open"
                >
                  Open <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
              {result.extracted && Object.values(result.extracted).some((v) => v != null && v !== "") && (
                <dl className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-[11px]">
                  {Object.entries(result.extracted).map(([k, v]) => {
                    if (v == null || v === "") return null;
                    return (
                      <div key={k} className="flex flex-col">
                        <dt className="text-slate-500 uppercase tracking-wide">{k.replace(/_/g, " ")}</dt>
                        <dd className="text-slate-800 font-medium">{String(v)}</dd>
                      </div>
                    );
                  })}
                </dl>
              )}
              <div className="mt-2 text-[10px] text-slate-400">Review before creating a request — ONE never auto-submits.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
